import React, { useState } from 'react';
import Styles from './PublicLabView.module.scss';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LabView from './LabList';

export default function InternshipList({ data }) {
    const [expandedCertId, setExpandedCertId] = useState(null);
    const [expandedLabIds, setExpandedLabIds] = useState({});
    const [expandedNodeIds, setExpandedNodeIds] = useState({});
    const [expandedTimelineNodes, setExpandedTimelineNodes] = useState({});
    const [expandedCourseId, setExpandedCourseId] = useState(null);

    // Toggle handlers
    const toggleCert = (certId) => {
        setExpandedCertId(expandedCertId === certId ? null : certId);
    };

    const toggleLab = (certId, labId, isExpanded) => {
        setExpandedLabIds((prev) => ({
            ...prev,
            [certId]: isExpanded ? null : labId,
        }));
    };

    const toggleNode = (labId, nodeId, isExpanded) => {
        setExpandedNodeIds((prev) => ({
            ...prev,
            [labId]: isExpanded ? null : nodeId,
        }));
        setExpandedTimelineNodes((prev) => ({
            ...prev,
            [nodeId]: false,
        }));
    };

    const toggleTimeline = (nodeId) => {
        setExpandedTimelineNodes((prev) => ({
            ...prev,
            [nodeId]: !prev[nodeId],
        }));
    };

    const toggleCourse = (courseId) => {
        setExpandedCourseId(expandedCourseId === courseId ? null : courseId);
    };

    // Expand button component
    const ExpandButton = ({ isExpanded, onClick, id }) => (
        <button
            id={id}
            className={Styles.expandButton}
            onClick={onClick}
        >
            {isExpanded ? (
                <ExpandLessIcon className={Styles.chevronIcon} fontSize="small" />
            ) : (
                <ExpandMoreIcon className={Styles.chevronIcon} fontSize="small" />
            )}
        </button>
    );

    // Header component for items (cert, lab, node, course, project)
    const ItemHeader = ({ item, isExpanded, onToggle, expandBtnId, prefix = 'item' }) => (
        <div
            id={`${prefix}Header_${item.id}`}
            className={prefix === 'cert' || prefix === 'course' || prefix === 'project' 
                ? Styles.labHeader 
                : Styles.nodeHeader}
            onClick={onToggle}
            style={{ cursor: "pointer" }}
        >
            <div id={`labLeft_${item.id}`} className={Styles.labLeft}>
                <div id={`${prefix === 'node' ? 'node' : 'lab'}IconContainer_${item.id}`} 
                     className={prefix === 'node' ? Styles.nodeIconContainer : Styles.labIconContainer}>
                    <img alt={item.name} src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${item.icon}`} />
                </div>
                <span id={`${prefix === 'node' ? 'node' : 'lab'}Title_${item.id}`} 
                      className={prefix === 'node' ? Styles.nodeTitle : Styles.labTitle}>
                    {item.name}
                </span>
            </div>
            <ExpandButton
                isExpanded={isExpanded}
                onClick={(e) => {
                    e.stopPropagation();
                    onToggle();
                }}
                id={expandBtnId}
            />
        </div>
    );

    // Node steps component
    const NodeSteps = ({ node, isExpanded }) => {
        const displayedSteps = expandedTimelineNodes[node.id]
            ? node.steps
            : node.steps.slice(0, 5);

        return (
            <>
                <ul id={`timelineList_${node.id}`} className={Styles.timelineList}>
                    {displayedSteps?.map((timeline, index) => (
                        <li
                            key={index}
                            id={`timelineItem_${node.id}_${index}`}
                            className={Styles.timelineItem}
                        >
                            {timeline}
                        </li>
                    ))}
                </ul>

                {node.steps.length > 5 && (
                    <button
                        id={`showMoreBtn_${node.id}`}
                        className={Styles.showMoreButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleTimeline(node.id);
                        }}
                    >
                        {expandedTimelineNodes[node.id] ? "Show Less" : "Show More"}
                    </button>
                )}
            </>
        );
    };

    // Node component
    const NodeCard = ({ node, labId, expandedNodeId }) => {
        const isNodeExpanded = expandedNodeId === node.id;

        return (
            <div
                key={node.id}
                id={`nodeCard_${node.id}`}
                className={`${Styles.nodeCard} ${isNodeExpanded ? Styles.nodeCardExpanded : ""}`}
            >
                <ItemHeader
                    item={node}
                    isExpanded={isNodeExpanded}
                    onToggle={() => toggleNode(labId, node.id, isNodeExpanded)}
                    expandBtnId={`nodeExpandBtn_${node.id}`}
                    prefix="node"
                />

                {isNodeExpanded && <NodeSteps node={node} isExpanded={isNodeExpanded} />}
            </div>
        );
    };

    // Lab component
    const LabCard = ({ lab, certId, expandedLabId, expandedNodeId }) => {
        const isLabExpanded = expandedLabId === lab.id;

        return (
            <div
                key={lab.id}
                id={`labCard_${lab.id}`}
                className={`${Styles.nodeCard} ${isLabExpanded ? Styles.nodeCardExpanded : ""}`}
            >
                <ItemHeader
                    item={lab}
                    isExpanded={isLabExpanded}
                    onToggle={() => toggleLab(certId, lab.id, isLabExpanded)}
                    expandBtnId={`nodeExpandBtn_${lab.id}`}
                    prefix="lab"
                />

                {isLabExpanded && (
                    <div id={`nodesGrid_${lab.id}`} className={Styles.nodesGrid}>
                        {lab.nodes?.map((node) => (
                            <NodeCard
                                key={node.id}
                                node={node}
                                labId={lab.id}
                                expandedNodeId={expandedNodeId}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Certification card component
    const CertificationCard = ({ cert }) => {
        const isCertExpanded = expandedCertId === cert.id;
        const expandedLabId = expandedLabIds[cert.id];

        return (
            <div key={cert.id} id={`certCard_${cert.id}`} className={Styles.labCard}>
                <ItemHeader
                    item={cert}
                    isExpanded={isCertExpanded}
                    onToggle={() => toggleCert(cert.id)}
                    expandBtnId={`labExpandBtn_${cert.id}`}
                    prefix="cert"
                />

                {isCertExpanded && (
                    <div id={`labGrid_${cert.id}`} className={Styles.nodesGrid}>
                        {cert.labs?.map((lab) => (
                            <LabCard
                                key={lab.id}
                                lab={lab}
                                certId={cert.id}
                                expandedLabId={expandedLabId}
                                expandedNodeId={expandedNodeIds[lab.id]}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Course card component
    const CourseCard = ({ course }) => {
        const isCourseExpanded = expandedCourseId === course.id;

        return (
            <div key={course.id} id={`courseCard_${course.id}`} className={Styles.labCard}>
                <ItemHeader
                    item={course}
                    isExpanded={isCourseExpanded}
                    onToggle={() => toggleCourse(course.id)}
                    expandBtnId={`labExpandBtn_${course.id}`}
                    prefix="course"
                />
                {isCourseExpanded && <LabView data={course} />}
            </div>
        );
    };

    // Project card component
    const ProjectCard = ({ project }) => {
        const isProjectExpanded = expandedCourseId === project.id;

        return (
            <div key={project.id} id={`courseCard_${project.id}`} className={Styles.labCard}>
                <ItemHeader
                    item={project}
                    isExpanded={isProjectExpanded}
                    onToggle={() => toggleCourse(project.id)}
                    expandBtnId={`labExpandBtn_${project.id}`}
                    prefix="project"
                />
                {isProjectExpanded && <LabView data={project} />}
            </div>
        );
    };

    // Section header component
    const SectionHeader = ({ title }) => (
        <p
            id='nodesIncludedTitle'
            className={Styles.labNameContainer}
            style={{ color: "black", padding: '0.5em 1em' }}
        >
            {title}
        </p>
    );

    return (
        <div id='internshipContainer' className={Styles.internshipContainer}>
            {/* Certifications Section */}
            {data?.certs?.length > 0 && (
                <>
                    <SectionHeader title="Certifications Included" />
                    {data.certs.map((cert) => (
                        <CertificationCard key={cert.id} cert={cert} />
                    ))}
                </>
            )}

            {/* Courses Section */}
            {data?.courses?.length > 0 && (
                <>
                    <SectionHeader title="Courses Included" />
                    {data.courses.map((course) => (
                        <CourseCard key={course.id} course={course} />
                    ))}
                </>
            )}

            {/* Projects Section */}
            {data?.projects?.length > 0 && (
                <>
                    <SectionHeader title="Projects Included" />
                    {data.projects.map((project) => (
                        <ProjectCard key={project.id} project={project} />
                    ))}
                </>
            )}
        </div>
    );
}